import React from 'react';
import { Box, Container, Typography, Card } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';

const UseCasesSection = () => {
  const useCases = [
    {
      icon: BoltIcon,
      title: 'Founders',
      accent: '#2563eb',
      gradient: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      border: '#bfdbfe',
      description:
        "Scale your startup with the right leadership. Find experienced executives who've navigated similar challenges and can drive growth.",
      items: [
        'Core team member and experienced managers',
        'Industry-specific expertise',
        'Growth stage leadership',
      ],
    },
    {
      icon: AttachMoneyIcon,
      title: 'Venture Capitalists',
      accent: '#7c3aed',
      gradient: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
      border: '#e9d5ff',
      description:
        'Evaluate portfolio company management and source executive talent for your investments. Make data-driven decisions with comprehensive insights.',
      items: [
        'Management team analysis',
        'Portfolio company support',
        'Due diligence insights',
      ],
    },
    {
      icon: PersonAddIcon,
      title: 'Executive Recruiters',
      accent: '#16a34a',
      gradient: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
      border: '#bbf7d0',
      description:
        'Discover experienced hires and seasoned executives beyond your existing network. Access global talent pools with unprecedented reach.',
      items: [
        'Global talent sourcing',
        'Candidate pipeline building',
        'Client delivery acceleration',
      ],
    },
  ];

  return (
    <Box id="use-cases" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            sx={{
              fontSize: { xs: '2.25rem', md: '2.75rem' },
              fontWeight: 800,
              color: '#111827',
              mb: 2,
            }}
          >
            Built for Decision Makers
          </Typography>

          <Typography
            sx={{
              fontSize: '1.125rem',
              color: '#6b7280',
              maxWidth: 820,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Whether you're a founder scaling your team, a VC evaluating management,
            or a recruiter sourcing talent, Getlista is made for you!
          </Typography>
        </Box>

        {/* CARDS */}
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          {useCases.map((useCase) => (
            <Card
              key={useCase.title}
              sx={{
                width: { xs: '100%', md: 'calc(33.333% - 22px)' },
                p: 4,
                borderRadius: 4,
                background: useCase.gradient,
                border: `1px solid ${useCase.border}`,
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 30px 60px rgba(0,0,0,0.08)',
                },
              }}
            >
              {/* ICON */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: useCase.accent,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <useCase.icon sx={{ color: '#ffffff', fontSize: 26 }} />
              </Box>

              {/* TITLE */}
              <Typography
                sx={{
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  color: '#111827',
                  mb: 1.5,
                }}
              >
                {useCase.title}
              </Typography>

              {/* DESCRIPTION */}
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  color: '#374151',
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                {useCase.description}
              </Typography>

              {/* LIST */}
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {useCase.items.map((item) => (
                  <Box
                    component="li"
                    key={item}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      mb: 1.2,
                    }}
                  >
                    <CheckIcon
                      sx={{
                        fontSize: 18,
                        color: useCase.accent,
                        mt: '3px',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        color: '#111827',
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default UseCasesSection;